<template>
	<div>
		<div v-if="show" class="c-search__auto-suggest c-global-search-autosuggestions__box" :class="isProductPage ? 'c-search__auto-suggest--product-page' : ''">
			<div v-show="isSuggestionState" class="c-global-search-autosuggestions__result-wrapper">
				<ul
					v-if="suggestions.length"
					class="c-global-search-autosuggestions__wrapper c-global-search-autosuggestions__results"
					id="products-autosuggest-list"
					aria-live="polite">
					<global-search-autoSuggest-item
						v-for="(card, index) in suggestions"
						:key="`card.heading-${index}`"
						:card="card"
						ref="card"
						:is-product="true"
						:selected="index === selectedIndex"
						:data-index="index"
						data-testid="auto-suggest-item"
						@itemClick="itemClick"
					/>
				</ul>
			</div>
			<global-search-did-you-mean v-if="isDidYouMeanState"
				@didYouMeanSearch="e => didYouMeanSearch(e)"
				:headline="labels.labels.noResultsHeadline"
				:text="labels.labels.didYouMeanText"
				:items="didYouMeanItems"
			></global-search-did-you-mean>
			<global-search-empty v-else-if="isEmptyState" :headline="labels.labels.noResultsHeadline" :text="labels.labels.noResultsText">
			</global-search-empty>

			<div class="c-global-search-autosuggestions__loading" v-show="isLoading && !isSuggestionState">
				<inline-spinner class="c-global-search-autosuggestions__spinner" size="lg"></inline-spinner>
			</div>
		</div>

	</div>
</template>

<script>
import { itemListIdAndName } from 'shared/settings/constants';
import GlobalSearchAutoSuggestItem from 'shared/components/globalSearch/globalSearchAutosuggestItem.vue';
import GlobalSearchDidYouMean from 'shared/components/globalSearch/globalSearchDidYouMean.vue';
import GlobalSearchEmpty from 'shared/components/globalSearch/globalSearchEmpty.vue';
import InlineSpinner from 'shared/components/inlineSpinner.vue';
import intersectionObserverMixin from 'shared/mixins/intersectionObserverMixin';
import {
	track_view_item_list,
	track_select_item,
} from 'shared/services/tracking/productTrackingService';
import useProductListLabels from 'shared/components/product/composables/useProductListLabels.js';
import { mapMutations } from 'vuex';

export default {
	name: 'auto-suggest',
	components: {
		GlobalSearchAutoSuggestItem,
		GlobalSearchDidYouMean,
		GlobalSearchEmpty,
		InlineSpinner,
	},
	mixins: [intersectionObserverMixin],
	props: [
		'suggestions',
		'propertyGroupDelimiter',
		'selectedIndex',
		'actionLabel',
		'isProductPage',
		'ecommerceTrackingData',
		'trackingEnabled',
		'noAutocompleteItems',
		'isLoading',
		'didYouMeanItems'
	],
	data() {
		return {
			labels: useProductListLabels(),
		};
	},
	methods: {
		...mapMutations(['setInteractionStarted', 'setResults']),
		itemClick(item) {
			this.executeAction(item);
		},
		didYouMeanSearch(term) {
			this.$emit('didYouMeanSearch', term);
		},
		async onProductsObserved(productElements) {
			const observedProducts = [];
			for (const { target, isIntersecting } of productElements) {
				if (!isIntersecting) continue;
				this.unregisterFromObserver(target);
				const i = Number(target.dataset.index);
				const product = this.suggestions[i];
				product.index = i;
				observedProducts.push(product);
			}
			if (observedProducts.length > 0) {
				track_view_item_list({
					productsDto: {
						globalTrackingParameters:
							this.ecommerceTrackingData?.value,
					},
					observedProducts,
					itemListIdAndName: itemListIdAndName.AUTO_SUGGEST_SEARCH,
				});
			}
		},
		async executeAction(product) {
			if (this.actionLabel) {
				this.setResults([product]);
			} else {
				if (!this.interactionStarted) {
					this.setInteractionStarted();
				}
				track_select_item(
					this.ecommerceTrackingData?.value,
					product,
					itemListIdAndName.AUTO_SUGGEST_SEARCH
				);

				globalThis.location.assign(product.url);
			}
		},
		registerItemsWithObserver() {
			this.$nextTick(() => {
				if (!this.observer) {
					this.initObserver();
				}
				if (this.$refs.card) {
					for (const component of this.$refs.card) {
						const el = component?.$el;
						if (el) {
							this.registerWithObserver(el);
						}
					}
				}
			});
		},
		initObserver() {
			this.intializeObserver(this.onProductsObserved, {
				threshold: 0.6,
			});
		}
	},
	computed: {
		isSuggestionState() {
			return (this.suggestions && this.suggestions.length > 0);
		},
		isDidYouMeanState() {
			return this.noAutocompleteItems && this.didYouMeanItems?.length > 0 && !this.isLoading;
		},
		isEmptyState() {
			return !this.isDidYouMeanState && this.noAutocompleteItems && !this.isLoading;
		},
		show() {
			return this.isSuggestionState || this.isDidYouMeanState || this.isEmptyState || this.isLoading;
		},
	},
	watch: {
		selectedIndex() {
			if (
				this.selectedIndex >= 0 &&
				this.$refs.suggestions?.[this.selectedIndex]
			) {
				this.$refs.suggestions[this.selectedIndex].$el.scrollIntoView();
			}
		},

		suggestions(newSuggestions) {
			if (this.trackingEnabled && newSuggestions?.length) {
				this.unregisterAllObservedItems();
				this.registerItemsWithObserver();
			}
		},
	},

	created() {
		if (this.trackingEnabled) {
			this.initObserver();
		}
	},
	mounted() {
		if (this.trackingEnabled) {
			this.registerItemsWithObserver();
		}
	},

	beforeUnmount() {
		this.observer?.disconnect();
	},
};
</script>
